import { authLogger } from '../../utils/loggerInstance';
import { supabaseProvider } from './supabaseProvider';
import {
  googleIdentityPlatformProvider,
  googleAuthProvider,
} from './googleIdentityPlatformProvider';
import { AUTH_CONFIG, validateAuthConfig } from '../../config/auth';
import { AuthProvider } from './providerInterface';

// Validate auth configuration on startup
validateAuthConfig();

// Constants
const TAG = '[AUTH]';

/**
 * Centralized session error handler
 * Handles authentication errors consistently across all providers
 *
 * @param error - The error object from authentication operations
 */
export function handleSessionError(error: Error | unknown): void {
  if (!error) return;

  const err = error as Record<string, unknown>;
  let message: string;
  if (error instanceof Error) {
    message = error.message;
  } else if (typeof err.message === 'string') {
    message = err.message;
  } else {
    message = 'Unknown error';
  }
  const code =
    (typeof err.code === 'string' ? err.code : undefined) ||
    (typeof err.status === 'string' || typeof err.status === 'number'
      ? String(err.status)
      : undefined) ||
    'unknown';
  const provider = typeof err.provider === 'string' ? err.provider : 'unknown';
  const stack = error instanceof Error ? error.stack : undefined;

  // Log the error with appropriate context
  authLogger.error('Authentication session error', {
    message,
    code,
    provider,
    stack,
  });

  // Handle specific error types that might require session cleanup
  if (
    code === 'invalid_token' ||
    code === 'expired_token' ||
    message.includes('invalid') ||
    message.includes('expired')
  ) {
    authLogger.warn('Session may be invalid, consider clearing local auth state');

    // Clear any cached auth tokens that might be invalid
    try {
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.removeItem('supabase.auth.token');
    } catch (storageError) {
      authLogger.warn('Failed to clear cached tokens', { error: storageError });
    }
  }
}

/**
 * Auth Provider Factory
 * Creates and configures the appropriate authentication provider
 * based on environment configuration
 */
export class AuthProviderFactory {
  private static providers: Record<string, AuthProvider> = {
    supabase: supabaseProvider,
    google_identity_platform: googleIdentityPlatformProvider,
    google_oauth: googleAuthProvider,
  };

  /**
   * Register a new authentication provider
   *
   * @param name - The name of the provider
   * @param provider - The provider instance implementing AuthProvider interface
   */
  public static registerProvider(name: string, provider: AuthProvider): void {
    this.providers[name.toLowerCase()] = provider;
  }

  /**
   * Get an authentication provider by name
   *
   * @param name - The name of the provider
   * @returns The provider instance
   */
  public static getProvider(name: string): AuthProvider {
    const provider = this.providers[name.toLowerCase()];
    if (!provider) {
      throw new Error(`Authentication provider '${name}' not found`);
    }
    return provider;
  }

  /**
   * Get the current authentication provider based on configuration
   *
   * @returns The current provider instance
   */
  public static getCurrentProvider(): AuthProvider {
    const providerName = AUTH_CONFIG.AUTH_PROVIDER;
    console.log(TAG, `Using authentication provider: ${providerName}`);
    return this.getProvider(providerName);
  }

  /**
   * Get all available provider names
   *
   * @returns Array of available provider names
   */
  public static getAvailableProviders(): string[] {
    return Object.keys(this.providers);
  }

  /**
   * Check if a provider is available
   *
   * @param name - The name of the provider
   * @returns True if the provider is available, false otherwise
   */
  public static isProviderAvailable(name: string): boolean {
    return name.toLowerCase() in this.providers;
  }
}

// Export the current auth provider based on configuration
export const authProvider = AuthProviderFactory.getCurrentProvider();
